/**
 * NODE PROPERTIES PANEL - Composant générique dynamique
 * 
 * Ce composant génère automatiquement l'interface de configuration
 * en fonction de la définition du nœud dans le Node Registry.
 * 
 * Aucune modification n'est nécessaire pour ajouter un nouveau type de nœud.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { X, Clock, RefreshCw, Calendar as CalendarIcon, Play, Code, ChevronDown, ChevronRight, Pencil, Eye } from 'lucide-react';
import axios from 'axios';
import { getNodeDefinition, SCRIPT_DEFINITIONS } from '../../config/nodeRegistry';
import AccountSelector from '../utilities/AccountSelector';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../../config/imageUrls';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  width: ${p => p.$wide ? '720px' : '600px'};
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(165, 145, 239, 0.5);
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  background: var(--card-bg);
  z-index: 10;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  
  &:hover {
    background: var(--card-border);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  &::placeholder {
    color: var(--text-muted);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
  }
  
  option {
    background: var(--surface);
    color: var(--text-primary);
  }
`;

const HelpText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-muted);
`;

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 8px;
`;

const PresetButton = styled.button`
  padding: 8px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  
  &:hover {
    background: var(--card-border);
    border-color: var(--primary);
    color: var(--text-primary);
  }
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: var(--card-bg);
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.$primary ? `
    background: var(--primary);
    color: var(--text-primary);
    
    &:hover {
      background: var(--secondary);
    }
  ` : `
    background: var(--card-border);
    color: var(--text-secondary);
    
    &:hover {
      background: var(--surface-hover);
      color: var(--text-primary);
    }
  `}
`;

const DeviceDropdown = styled.div`
  position: relative;
`;

const DeviceDropdownBtn = styled.button`
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; background: var(--surface); border: 1px solid var(--card-border);
  border-radius: 6px; color: var(--text-primary); font-size: 14px; cursor: pointer;
  text-align: left; transition: all 0.2s;
  &:focus { outline: none; border-color: var(--primary); }
  img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; flex-shrink: 0; border: 2px solid var(--primary); }
`;

const DeviceDropdownList = styled.div`
  position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px;
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,.15); max-height: 400px; overflow-y: auto; z-index: 100;
`;

const DeviceDropdownItem = styled.button`
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border: none; background: ${p => p.$sel ? 'rgba(165,145,239,.1)' : 'transparent'};
  color: var(--text-primary); font-size: 13px; cursor: pointer; text-align: left;
  &:hover { background: var(--surface-hover); }
  img { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
    border: 2px solid ${p => p.$sel ? 'var(--primary)' : 'var(--card-border)'}; }
`;

const DeviceDropdownInfo = styled.div`
  flex: 1; min-width: 0;
  .dd-name { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .dd-user { font-size: 11px; color: var(--primary); font-weight: 600; }
  .dd-model { font-size: 10px; color: var(--text-muted); }
`;

const OutputPanel = styled.div`
  margin-top: 16px;
  border: 1px solid ${p => p.$error ? '#ef4444' : '#22c55e'};
  border-radius: 8px;
  overflow: hidden;
`;

const OutputHeader = styled.div`
  padding: 8px 12px;
  background: ${p => p.$error ? 'rgba(239,68,68,.1)' : 'rgba(34,197,94,.1)'};
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.$error ? '#ef4444' : '#22c55e'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OutputBody = styled.pre`
  padding: 10px 12px;
  margin: 0;
  font-size: 12px;
  font-family: 'Fira Code', 'Consolas', monospace;
  color: var(--text-primary);
  background: var(--surface);
  max-height: 200px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const TestButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  &:hover { filter: brightness(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const IOSection = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: rgba(99,102,241,.06);
  border: 1px solid rgba(99,102,241,.15);
  border-radius: 8px;
  font-size: 12px;
  color: var(--text-secondary);
`;

/* ── Code Node Styled Components ── */
const CodeEditorWrap = styled.div`
  position: relative;
  border: 1px solid var(--card-border);
  border-radius: 8px;
  overflow: hidden;
  &:focus-within { border-color: var(--primary); }
`;

const CodeEditorTextarea = styled.textarea`
  width: 100%;
  padding: 14px 14px 14px 48px;
  background: #1a1b2e;
  color: #e2e8f0;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  border: none;
  resize: vertical;
  min-height: ${p => (p.$rows || 12) * 22}px;
  tab-size: 2;
  &:focus { outline: none; }
  &::placeholder { color: #4a5568; }
`;

const CodeLineNumbers = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 40px;
  padding: 14px 8px 14px 0;
  text-align: right;
  font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #4a5568;
  user-select: none;
  pointer-events: none;
  background: rgba(0,0,0,.15);
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
`;

const TemplateCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:hover {
    border-color: #8b5cf6;
    background: rgba(139,92,246,.06);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(139,92,246,.15);
  }
  .tc-label { font-size: 13px; font-weight: 700; color: var(--text-primary); }
  .tc-desc { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
`;

const TemplateSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NodeTabBar = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 16px;
  background: var(--surface);
  border-radius: 8px;
  padding: 3px;
`;

const NodeTab = styled.button`
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  &:hover { color: ${p => p.$active ? '#fff' : 'var(--text-primary)'}; }
`;

const InputDataPanel = styled.div`
  margin-top: 8px;
  border: 1px solid rgba(99,102,241,.2);
  border-radius: 8px;
  overflow: hidden;
`;

const InputDataHeader = styled.div`
  padding: 8px 12px;
  background: rgba(99,102,241,.08);
  font-size: 12px;
  font-weight: 700;
  color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const InputDataBody = styled.pre`
  padding: 10px 12px;
  margin: 0;
  font-size: 12px;
  font-family: 'Fira Code', 'Consolas', monospace;
  color: var(--text-primary);
  background: var(--surface);
  max-height: 150px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const InputEditTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  margin: 0;
  font-size: 12px;
  font-family: 'Fira Code', 'Consolas', monospace;
  color: var(--text-primary);
  background: var(--surface);
  border: none;
  resize: vertical;
  min-height: 80px;
  &:focus { outline: none; }
`;

const OutputTabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--card-border);
`;

const OutputTab = styled.button`
  padding: 6px 14px;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? '#22c55e' : 'transparent'};
  background: transparent;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  color: ${p => p.$active ? '#22c55e' : 'var(--text-muted)'};
  transition: all 0.15s;
  &:hover { color: ${p => p.$active ? '#22c55e' : 'var(--text-primary)'}; }
`;

const ConsoleOutput = styled.pre`
  padding: 10px 12px;
  margin: 0;
  font-size: 11px;
  font-family: 'Fira Code', 'Consolas', monospace;
  color: #94a3b8;
  background: #0f172a;
  max-height: 150px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

/* ── Cron Builder Styled Components ── */
const CronModeTabs = styled.div`
  display: flex;
  gap: 3px;
  margin-bottom: 16px;
  background: var(--surface);
  border-radius: 8px;
  padding: 3px;
`;
const CronModeTab = styled.button`
  flex: 1;
  padding: 8px 4px;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${p => p.$active ? 'var(--primary)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  &:hover { color: var(--text-primary); }
`;
const CronTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;
const CronTimeSelect = styled.select`
  padding: 10px 8px;
  background: var(--surface);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 700;
  font-family: 'JetBrains Mono', monospace;
  cursor: pointer;
  text-align: center;
  min-width: 70px;
  &:focus { outline: none; border-color: var(--primary); }
  option { background: var(--card-bg); }
`;
const CronDaysRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
`;
const CronDayBtn = styled.button`
  flex: 1;
  padding: 8px 0;
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  border-radius: 8px;
  background: ${p => p.$active ? 'rgba(165,145,239,.15)' : 'var(--surface)'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: var(--primary); }
`;
const CronPreviewLine = styled.div`
  padding: 8px 12px;
  background: rgba(165,145,239,.06);
  border: 1px solid rgba(165,145,239,.15);
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  code { font-family: 'JetBrains Mono', monospace; color: var(--primary); font-weight: 700; }
`;

/* ── Cron Builder Helpers ── */
function parseCronExpression(expr) {
  const defaults = { mode: 'daily', hour: 9, minute: 0, days: [], dayOfMonth: 1, intervalValue: 2, intervalUnit: 'hours' };
  if (!expr) return defaults;
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return { ...defaults, mode: 'custom' };
  const [min, hour, dom, , dow] = parts;
  if (min.startsWith('*/') && hour === '*') return { ...defaults, mode: 'interval', intervalValue: parseInt(min.slice(2)) || 30, intervalUnit: 'minutes' };
  if (hour.startsWith('*/') && dom === '*') return { ...defaults, mode: 'interval', intervalValue: parseInt(hour.slice(2)) || 2, intervalUnit: 'hours', minute: parseInt(min) || 0 };
  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && dow === '*') return { ...defaults, mode: 'daily', hour: parseInt(hour), minute: parseInt(min) };
  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && dom === '*' && /^[\d,]+$/.test(dow)) return { ...defaults, mode: 'weekly', hour: parseInt(hour), minute: parseInt(min), days: dow.split(',').map(Number) };
  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && /^\d+$/.test(dom) && dow === '*') return { ...defaults, mode: 'monthly', hour: parseInt(hour), minute: parseInt(min), dayOfMonth: parseInt(dom) };
  return { ...defaults, mode: 'custom' };
}

function buildCronExpression(mode, { hour, minute, days, dayOfMonth, intervalValue, intervalUnit }) {
  switch (mode) {
    case 'daily': return `${minute} ${hour} * * *`;
    case 'weekly': return days.length > 0 ? `${minute} ${hour} * * ${days.sort((a, b) => a - b).join(',')}` : `${minute} ${hour} * * *`;
    case 'monthly': return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'interval': return intervalUnit === 'minutes' ? `*/${intervalValue} * * * *` : `0 */${intervalValue} * * *`;
    default: return null;
  }
}

function describeCron(expr) {
  if (!expr) return '';
  const p = parseCronExpression(expr);
  const pad = n => String(n).padStart(2, '0');
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  switch (p.mode) {
    case 'daily': return `Tous les jours à ${pad(p.hour)}:${pad(p.minute)}`;
    case 'weekly': return `${p.days.map(d => dayNames[d]).join(', ')} à ${pad(p.hour)}:${pad(p.minute)}`;
    case 'monthly': return `Le ${p.dayOfMonth} de chaque mois à ${pad(p.hour)}:${pad(p.minute)}`;
    case 'interval': return p.intervalUnit === 'minutes' ? `Toutes les ${p.intervalValue} minutes` : `Toutes les ${p.intervalValue} heures`;
    default: return expr;
  }
}

/* ── Cron Builder Component ── */
function CronBuilder({ value, onChange, presets }) {
  const [init] = useState(() => parseCronExpression(value));
  const [cronMode, setCronMode] = useState(init.mode);
  const [hour, setHour] = useState(init.hour);
  const [minute, setMinute] = useState(init.minute);
  const [days, setDays] = useState(init.days);
  const [dayOfMonth, setDayOfMonth] = useState(init.dayOfMonth);
  const [intervalValue, setIntervalValue] = useState(init.intervalValue);
  const [intervalUnit, setIntervalUnit] = useState(init.intervalUnit);
  const [rawCron, setRawCron] = useState(value || '0 9 * * *');

  useEffect(() => {
    if (!value) onChange('0 9 * * *');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (m, h, min, d, dom, iv, iu) => {
    const expr = buildCronExpression(m, { hour: h, minute: min, days: d, dayOfMonth: dom, intervalValue: iv, intervalUnit: iu });
    if (expr) { setRawCron(expr); onChange(expr); }
  };

  const applyPreset = (cronExpr) => {
    const p = parseCronExpression(cronExpr);
    setCronMode(p.mode); setHour(p.hour); setMinute(p.minute); setDays(p.days);
    setDayOfMonth(p.dayOfMonth); setIntervalValue(p.intervalValue); setIntervalUnit(p.intervalUnit);
    setRawCron(cronExpr); onChange(cronExpr);
  };

  const changeMode = (m) => { setCronMode(m); if (m !== 'custom') emit(m, hour, minute, days, dayOfMonth, intervalValue, intervalUnit); };
  const changeHour = (h) => { setHour(h); emit(cronMode, h, minute, days, dayOfMonth, intervalValue, intervalUnit); };
  const changeMinute = (m) => { setMinute(m); emit(cronMode, hour, m, days, dayOfMonth, intervalValue, intervalUnit); };
  const toggleDay = (d) => { const nd = days.includes(d) ? days.filter(x => x !== d) : [...days, d]; setDays(nd); emit(cronMode, hour, minute, nd, dayOfMonth, intervalValue, intervalUnit); };
  const changeDayOfMonth = (d) => { setDayOfMonth(d); emit(cronMode, hour, minute, days, d, intervalValue, intervalUnit); };
  const changeInterval = (v, u) => { setIntervalValue(v); setIntervalUnit(u); emit(cronMode, hour, minute, days, dayOfMonth, v, u); };

  const pad = n => String(n).padStart(2, '0');
  const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const DAY_VALUES = [1, 2, 3, 4, 5, 6, 0];

  const MODES = [
    { id: 'daily', label: '⏰ Heure fixe' },
    { id: 'interval', label: '🔄 Récurrence' },
    { id: 'weekly', label: '📅 Jours' },
    { id: 'monthly', label: '📆 Mensuel' },
    { id: 'custom', label: '✏️ Manuel' },
  ];

  const currentExpr = cronMode === 'custom' ? rawCron : (buildCronExpression(cronMode, { hour, minute, days, dayOfMonth, intervalValue, intervalUnit }) || rawCron);

  return (
    <>
      <CronModeTabs>
        {MODES.map(m => (
          <CronModeTab key={m.id} $active={cronMode === m.id} onClick={() => changeMode(m.id)}>{m.label}</CronModeTab>
        ))}
      </CronModeTabs>

      {cronMode === 'daily' && (
        <CronTimeRow>
          <Clock size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <CronTimeSelect value={hour} onChange={e => changeHour(parseInt(e.target.value))}>
            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
          </CronTimeSelect>
          <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-muted)' }}>:</span>
          <CronTimeSelect value={minute} onChange={e => changeMinute(parseInt(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i * 5).map(m => <option key={m} value={m}>{pad(m)}</option>)}
          </CronTimeSelect>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 8 }}>tous les jours</span>
        </CronTimeRow>
      )}

      {cronMode === 'interval' && (
        <CronTimeRow>
          <RefreshCw size={18} style={{ color: '#14b8a6', flexShrink: 0 }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: 13, whiteSpace: 'nowrap' }}>Toutes les</span>
          <CronTimeSelect style={{ fontSize: 16 }} value={intervalValue} onChange={e => changeInterval(parseInt(e.target.value), intervalUnit)}>
            {(intervalUnit === 'minutes' ? [5, 10, 15, 20, 30, 45] : [1, 2, 3, 4, 6, 8, 12]).map(v => <option key={v} value={v}>{v}</option>)}
          </CronTimeSelect>
          <Select style={{ width: 'auto', minWidth: 100 }} value={intervalUnit} onChange={e => {
            const u = e.target.value;
            const v = u === 'minutes' ? 30 : 2;
            changeInterval(v, u);
          }}>
            <option value="minutes">minutes</option>
            <option value="hours">heures</option>
          </Select>
        </CronTimeRow>
      )}

      {cronMode === 'weekly' && (
        <>
          <CronDaysRow>
            {DAY_LABELS.map((label, i) => (
              <CronDayBtn key={i} $active={days.includes(DAY_VALUES[i])} onClick={() => toggleDay(DAY_VALUES[i])}>{label}</CronDayBtn>
            ))}
          </CronDaysRow>
          <CronTimeRow>
            <Clock size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
            <CronTimeSelect value={hour} onChange={e => changeHour(parseInt(e.target.value))}>
              {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
            </CronTimeSelect>
            <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-muted)' }}>:</span>
            <CronTimeSelect value={minute} onChange={e => changeMinute(parseInt(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i * 5).map(m => <option key={m} value={m}>{pad(m)}</option>)}
            </CronTimeSelect>
          </CronTimeRow>
        </>
      )}

      {cronMode === 'monthly' && (
        <CronTimeRow style={{ flexWrap: 'wrap' }}>
          <CalendarIcon size={18} style={{ color: '#f59e0b', flexShrink: 0 }} />
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Le</span>
          <CronTimeSelect style={{ fontSize: 16, minWidth: 60 }} value={dayOfMonth} onChange={e => changeDayOfMonth(parseInt(e.target.value))}>
            {Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
          </CronTimeSelect>
          <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>du mois à</span>
          <CronTimeSelect value={hour} onChange={e => changeHour(parseInt(e.target.value))}>
            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
          </CronTimeSelect>
          <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-muted)' }}>:</span>
          <CronTimeSelect value={minute} onChange={e => changeMinute(parseInt(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i * 5).map(m => <option key={m} value={m}>{pad(m)}</option>)}
          </CronTimeSelect>
        </CronTimeRow>
      )}

      {cronMode === 'custom' && (
        <Input
          type="text"
          value={rawCron}
          onChange={e => { setRawCron(e.target.value); onChange(e.target.value); }}
          placeholder="0 9 * * *"
          style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, letterSpacing: 2 }}
        />
      )}

      {presets && presets.length > 0 && (
        <PresetGrid style={{ marginTop: 12 }}>
          {presets.map(p => (
            <PresetButton key={p.value} onClick={() => applyPreset(p.value)}>{p.label}</PresetButton>
          ))}
        </PresetGrid>
      )}

      <CronPreviewLine>
        <span style={{ color: 'var(--text-muted)' }}>{describeCron(currentExpr)}</span>
        <code>{currentExpr}</code>
      </CronPreviewLine>
    </>
  );
}

const NodePropertiesPanel = ({ node, onClose, onSave, devices = [], accounts = [], deviceAccounts = [], schedulerWorkflowLetter = null, previousNodeOutput = null, onTestOutput = null }) => {
  const [config, setConfig] = useState({});
  const [deviceDropOpen, setDeviceDropOpen] = useState(false);
  const [restarterDropOpen, setRestarterDropOpen] = useState(false);
  const [workflowLetters, setWorkflowLetters] = useState([]);
  const [testOutput, setTestOutput] = useState(null);
  const [testing, setTesting] = useState(false);
  const [codeTab, setCodeTab] = useState('code');
  const [inputDataStr, setInputDataStr] = useState('{}');
  const [editingInput, setEditingInput] = useState(false);
  const [outputViewTab, setOutputViewTab] = useState('json');
  const [consoleLogs, setConsoleLogs] = useState([]);
  const codeTextareaRef = useRef(null);
  const [cardParams, setCardParams] = useState(null);
  const [accountTargets, setAccountTargets] = useState([]);
  const [cardParamsLoading, setCardParamsLoading] = useState(false);
  const cardParamsLoadedRef = useRef(null); // track which device+account combo was loaded
  
  useEffect(() => {
    if (node?.data?.config) {
      setConfig(node.data.config);
    }
    setTestOutput(null);
    setConsoleLogs([]);
    setCodeTab('code');
    setOutputViewTab('json');
    // Reset DB load cache so params are re-fetched for this node
    cardParamsLoadedRef.current = null;
    setCardParams(null);
    setAccountTargets([]);
    // Initialize input data from previous node output if available
    const initInput = previousNodeOutput || {};
    setInputDataStr(JSON.stringify(initInput, null, 2));
    setEditingInput(false);
  }, [node, previousNodeOutput]);

  useEffect(() => {
    axios.get('/api/workflow-builder/workflow-letters')
      .then(r => setWorkflowLetters(r.data.letters || []))
      .catch(() => {});
  }, []);

  // Fetch card params from DB when device+account is selected and script type requires it
  useEffect(() => {
    if (!config.device_id || !config.account_id || !config.script_type) return;
    const scriptDef = SCRIPT_DEFINITIONS[config.script_type];
    if (!scriptDef?.loadFromDB) return;
    const key = `${config.device_id}:${config.account_id}:${config.app_id || ''}:${config.script_type}`;
    if (cardParamsLoadedRef.current === key) return;
    cardParamsLoadedRef.current = key;
    setCardParamsLoading(true);
    const appId = config.app_id || 'com.instagram.android';
    axios.get(`/api/workflow-builder/card-params/${config.device_id}/${config.account_id}?app_id=${encodeURIComponent(appId)}`)
      .then(r => {
        if (r.data.success && r.data.params) {
          setCardParams(r.data.params);
          setAccountTargets(r.data.targets || []);
          const p = r.data.params;
          const origConfig = node?.data?.config || {};
          // _params_customized flag: set by onSave when user explicitly saves custom params.
          // If present, user has previously saved → preserve their values, only fill gaps from DB.
          // If absent, this is a first-time config → apply all DB values.
          const userHasSaved = !!origConfig._params_customized;
          setConfig(prev => {
            const next = { ...prev };
            if (userHasSaved) {
              // Only fill fields NOT already in the saved config
              for (const [field, val] of Object.entries(p)) {
                if (val != null && field !== 'app_id' && origConfig[field] == null) {
                  next[field] = val;
                }
              }
            } else {
              // First time: apply all DB values
              for (const [field, val] of Object.entries(p)) {
                if (val != null && field !== 'app_id') {
                  next[field] = val;
                }
              }
            }
            // Apply script definition defaults for any field still missing
            if (scriptDef?.parameters) {
              for (const param of scriptDef.parameters) {
                if (next[param.name] == null && param.defaultValue != null) {
                  next[param.name] = param.defaultValue;
                }
              }
            }
            // Default: use pool targets
            if (!next.targets) {
              next._use_pool_targets = true;
            }
            return next;
          });
        } else {
          setCardParams(null);
          setAccountTargets([]);
        }
      })
      .catch(() => { setCardParams(null); setAccountTargets([]); })
      .finally(() => setCardParamsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.device_id, config.account_id, config.script_type, config.app_id]);

  const getInputData = useCallback(() => {
    try { return JSON.parse(inputDataStr); } catch { return {}; }
  }, [inputDataStr]);

  const runTest = async () => {
    if (!node) return;
    setTesting(true);
    setTestOutput(null);
    setConsoleLogs([]);
    try {
      const res = await axios.post('/api/workflow-builder/test-node', {
        type: node.data.type,
        config: config,
        label: node.data.label || node.data.type,
        input: getInputData()
      });
      const output = res.data.output || res.data;
      setTestOutput(output);
      if (res.data.consoleLogs) setConsoleLogs(res.data.consoleLogs);
      if (output && output.success && onTestOutput) onTestOutput(output);
      setCodeTab('output');
      setOutputViewTab('json');
    } catch (err) {
      setTestOutput({ success: false, error: err.response?.data?.error || err.message });
      setCodeTab('output');
    }
    setTesting(false);
  };

  const applyTemplate = (template) => {
    setConfig(prev => ({
      ...prev,
      script: template.code,
      language: template.language || 'javascript'
    }));
    setCodeTab('code');
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      updateConfig('script', newValue);
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    }
  };
  
  if (!node) return null;
  
  const nodeDefinition = getNodeDefinition(node.data.type);
  
  if (!nodeDefinition) {
    console.error(`Node definition not found for type: ${node.data.type}`);
    return null;
  }
  
  const handleSave = () => {
    // Mark that user has explicitly customized params — so they persist on re-open
    const saveConfig = { ...config, _params_customized: true };
    onSave(node.id, saveConfig);
    onClose();
  };
  
  const updateConfig = (name, value) => {
    setConfig(prev => {
      const next = { ...prev, [name]: value };
      // Auto-resolve account_id + app_id when device is selected via device-account-select
      if (name === 'device_id' && deviceAccounts.length > 0) {
        const match = deviceAccounts.find(da => da.device_id === value);
        if (match) {
          next.account_id = match.account_id;
          next.app_id = match.app_id || 'com.instagram.android';
          next._device_name = match.device_name;
          next._instagram_username = match.instagram_username;
        }
      }
      return next;
    });
  };

  // Select a specific device+account+app_id combo from the dropdown
  const selectDeviceAccount = (da) => {
    setConfig(prev => ({
      ...prev,
      device_id: da.device_id,
      account_id: da.account_id,
      app_id: da.app_id || 'com.instagram.android',
      _device_name: da.device_name,
      _instagram_username: da.instagram_username,
    }));
  };
  
  // Vérifier si un paramètre doit être affiché selon les conditions
  const shouldShowParameter = (param) => {
    // Hide params that require a device when none selected
    if (param.requiresDevice && !config.device_id) return false;
    // showIf: { field: 'xxx', value: 'yyy' } — show only when config[field] === value
    if (param.showIf) {
      const { field, value } = param.showIf;
      const currentVal = config[field] !== undefined ? config[field] : (nodeDefinition?.parameters?.find(p => p.name === field)?.defaultValue);
      if (currentVal !== value) return false;
    }
    if (!param.showWhen) return true;
    const conditions = Object.entries(param.showWhen);
    return conditions.every(([key, value]) => config[key] === value);
  };
  
  // Rendu d'un champ selon son type
  const renderField = (param) => {
    if (!shouldShowParameter(param)) return null;
    
    const Icon = param.icon;
    
    switch (param.type) {
      case 'cron-builder':
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
            </Label>
            <CronBuilder
              value={config[param.name] || param.defaultValue || ''}
              onChange={(v) => updateConfig(param.name, v)}
              presets={param.presets}
            />
          </FormGroup>
        );

      case 'code-editor': {
        const codeValue = config[param.name] || param.defaultValue || '';
        const lineCount = (codeValue || '').split('\n').length;
        return (
          <FormGroup key={param.name}>
            <Label>
              <Code size={16} />
              {param.label}
            </Label>
            <CodeEditorWrap>
              <CodeLineNumbers>
                {Array.from({ length: Math.max(lineCount, 1) }, (_, i) => (
                  <div key={i}>{i + 1}</div>
                ))}
              </CodeLineNumbers>
              <CodeEditorTextarea
                ref={codeTextareaRef}
                $rows={param.rows || 12}
                value={codeValue}
                onChange={(e) => updateConfig(param.name, e.target.value)}
                onKeyDown={handleCodeKeyDown}
                placeholder={param.placeholder}
                spellCheck={false}
              />
            </CodeEditorWrap>
            <HelpText>
              Utilisez <code style={{ color: '#8b5cf6', fontWeight: 700 }}>input</code> pour accéder aux données du nœud précédent.
              Définissez les propriétés de <code style={{ color: '#8b5cf6', fontWeight: 700 }}>result</code> pour passer des données au nœud suivant.
            </HelpText>
          </FormGroup>
        );
      }

      case 'text':
      case 'number':
      case 'datetime-local':
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <Input
              type={param.type}
              value={config[param.name] || param.defaultValue || ''}
              onChange={(e) => updateConfig(param.name, param.type === 'number' ? parseInt(e.target.value) : e.target.value)}
              placeholder={param.placeholder}
              min={param.min}
              max={param.max}
            />
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
            {param.presets && (
              <PresetGrid>
                {param.presets.map(preset => (
                  <PresetButton
                    key={preset.value}
                    onClick={() => updateConfig(param.name, preset.value)}
                  >
                    {preset.label}
                  </PresetButton>
                ))}
              </PresetGrid>
            )}
          </FormGroup>
        );
      
      case 'textarea':
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <TextArea
              value={config[param.name] || param.defaultValue || ''}
              onChange={(e) => updateConfig(param.name, e.target.value)}
              placeholder={param.placeholder}
              rows={param.rows || 4}
            />
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      
      case 'select':
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <Select
              value={config[param.name] || param.defaultValue || ''}
              onChange={(e) => updateConfig(param.name, e.target.value)}
            >
              {(param.defaultValue === '' || param.defaultValue === undefined) && (
                <option value="">-- Choisir --</option>
              )}
              {param.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      
      case 'workflow-letter-select': {
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
            </Label>
            <Select
              value={config[param.name] || ''}
              onChange={(e) => updateConfig(param.name, e.target.value)}
            >
              <option value="">-- Toutes les devices --</option>
              {workflowLetters.map(letter => (
                <option key={letter} value={letter}>
                  Workflow {letter}
                </option>
              ))}
            </Select>
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      }

      case 'device-account-select': {
        const hasDeviceAccounts = deviceAccounts && deviceAccounts.length > 0;
        let items = hasDeviceAccounts ? deviceAccounts : devices.map(d => ({
          device_id: d.device_id || d.id,
          device_name: d.device_name || d.name || d.device_id || d.id,
          android_version: d.android_version || '',
          instagram_username: null,
          account_id: null,
          app_id: 'com.instagram.android'
        }));
        // Filter by workflow letter from scheduler node if set
        const filterLetter = schedulerWorkflowLetter;
        if (filterLetter) {
          items = items.filter(da => da.workflow_letter && da.workflow_letter.toUpperCase() === filterLetter.toUpperCase());
        }
        // Match selected item by device_id + account_id (since multiple accounts per device)
        const selectedItem = items.find(da =>
          da.device_id === config[param.name] &&
          ((!config.account_id && !da.account_id) || String(da.account_id) === String(config.account_id))
        ) || items.find(da => da.device_id === config[param.name]);
        const isNonDefaultPkg = (appId) => appId && appId !== 'com.instagram.android';
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <DeviceDropdown>
              <DeviceDropdownBtn type="button" onClick={() => setDeviceDropOpen(!deviceDropOpen)}>
                {selectedItem ? (
                  <>
                    {selectedItem.instagram_username && (
                      <img src={getLocalProfilePicUrl(selectedItem.instagram_username)} alt=""
                        onError={e => {
                          const step = parseInt(e.target.dataset.fb || '0');
                          if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(selectedItem.instagram_username); return; }
                          if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${selectedItem.instagram_username}`; return; }
                          e.target.style.display = 'none';
                        }} />
                    )}
                    <DeviceDropdownInfo>
                      <div className="dd-name">{selectedItem.device_name || selectedItem.device_id}</div>
                      {selectedItem.instagram_username && <div className="dd-user">@{selectedItem.instagram_username}</div>}
                    </DeviceDropdownInfo>
                    {isNonDefaultPkg(selectedItem.app_id) && (
                      <span style={{ marginLeft: 'auto', fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,.15)', color: '#f59e0b', fontWeight: 700, fontFamily: 'monospace' }}>
                        {selectedItem.app_id.replace('com.instagram.', '')}
                      </span>
                    )}
                  </>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>-- Choisir un device / compte --</span>
                )}
              </DeviceDropdownBtn>
              {deviceDropOpen && (
                <DeviceDropdownList>
                  {items.map((da, i) => (
                    <DeviceDropdownItem
                      key={`${da.device_id}-${da.account_id || ''}-${da.app_id || ''}-${i}`}
                      $sel={da.device_id === config[param.name] && String(da.account_id) === String(config.account_id)}
                      type="button"
                      onClick={() => {
                        selectDeviceAccount(da);
                        setDeviceDropOpen(false);
                      }}
                    >
                      {da.instagram_username ? (
                        <img src={getLocalProfilePicUrl(da.instagram_username)} alt=""
                          onError={e => {
                            const step = parseInt(e.target.dataset.fb || '0');
                            if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(da.instagram_username); return; }
                            if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${da.instagram_username}`; return; }
                            e.target.style.display = 'none';
                          }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>
                          📱
                        </div>
                      )}
                      <DeviceDropdownInfo>
                        <div className="dd-name">{da.device_name || da.device_id}</div>
                        {da.instagram_username && <div className="dd-user">@{da.instagram_username}</div>}
                        {da.android_version && <div className="dd-model">Android {da.android_version}</div>}
                      </DeviceDropdownInfo>
                      {isNonDefaultPkg(da.app_id) && (
                        <span style={{ marginLeft: 'auto', fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,.15)', color: '#f59e0b', fontWeight: 700, fontFamily: 'monospace', flexShrink: 0 }}>
                          {da.app_id.replace('com.instagram.', '')}
                        </span>
                      )}
                    </DeviceDropdownItem>
                  ))}
                </DeviceDropdownList>
              )}
            </DeviceDropdown>
            {config[param.name] && selectedItem && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(34,197,94,.08)', borderRadius: 6, fontSize: 12, color: '#22c55e', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {selectedItem.instagram_username && (
                  <img src={getLocalProfilePicUrl(selectedItem.instagram_username)} alt=""
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                    onError={e => {
                      const step = parseInt(e.target.dataset.fb || '0');
                      if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(selectedItem.instagram_username); return; }
                      if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${selectedItem.instagram_username}`; return; }
                      e.target.style.display = 'none';
                    }} />
                )}
                Device: {selectedItem.device_name || config[param.name]}
                {selectedItem.instagram_username && ` — @${selectedItem.instagram_username}`}
                {(config.account_id || selectedItem.account_id) && ` (account #${config.account_id || selectedItem.account_id})`}
                {isNonDefaultPkg(config.app_id) && (
                  <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: 'rgba(245,158,11,.15)', color: '#f59e0b', fontFamily: 'monospace' }}>
                    {config.app_id}
                  </span>
                )}
              </div>
            )}
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      }

      case 'restarter-device-select': {
        let restarterItems = (deviceAccounts.length > 0 ? deviceAccounts : devices.map(d => ({
          device_id: d.device_id || d.id,
          device_name: d.device_name || d.name || d.device_id || d.id,
          is_restarter: d.is_restarter,
          workflow_letter: d.workflow_letter || d.workflow
        })));
        // Filter by workflow letter from scheduler node (same as device nodes)
        const restarterFilterLetter = schedulerWorkflowLetter;
        if (restarterFilterLetter) {
          restarterItems = restarterItems.filter(da => da.workflow_letter && da.workflow_letter.toUpperCase() === restarterFilterLetter.toUpperCase());
        }
        const restarterDevices = restarterItems.filter(d => d.is_restarter);
        const selectedRestarter = restarterDevices.find(d => d.device_id === config[param.name]);
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <DeviceDropdown>
              <DeviceDropdownBtn type="button" onClick={() => setRestarterDropOpen(!restarterDropOpen)}>
                {selectedRestarter ? (
                  <DeviceDropdownInfo>
                    <div className="dd-name">{selectedRestarter.device_name || selectedRestarter.device_id}</div>
                    <div className="dd-model" style={{ color: '#f59e0b' }}>🔄 Restarter</div>
                  </DeviceDropdownInfo>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>-- Choisir un restarter --</span>
                )}
              </DeviceDropdownBtn>
              {restarterDropOpen && (
                <DeviceDropdownList>
                  {restarterDevices.length === 0 && (
                    <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: 12 }}>Aucun device restarter trouvé</div>
                  )}
                  {restarterDevices.map((d, i) => (
                    <DeviceDropdownItem
                      key={`${d.device_id}-${i}`}
                      $sel={d.device_id === config[param.name]}
                      type="button"
                      onClick={() => {
                        updateConfig(param.name, d.device_id);
                        setConfig(prev => ({ ...prev, [param.name]: d.device_id, _restarter_name: d.device_name }));
                        setRestarterDropOpen(false);
                      }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(245,158,11,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                        🔄
                      </div>
                      <DeviceDropdownInfo>
                        <div className="dd-name">{d.device_name || d.device_id}</div>
                        <div className="dd-model" style={{ color: '#f59e0b' }}>Restarter</div>
                      </DeviceDropdownInfo>
                    </DeviceDropdownItem>
                  ))}
                </DeviceDropdownList>
              )}
            </DeviceDropdown>
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      }

      case 'account-select':
        return (
          <AccountSelector
            key={param.name}
            accounts={accounts}
            value={config[param.name] || ''}
            onChange={(value) => updateConfig(param.name, value)}
            label={param.label}
          />
        );
      
      case 'device-select':
        return (
          <FormGroup key={param.name}>
            <Label>
              {Icon && <Icon size={16} />}
              {param.label}
              {param.required && <span style={{ color: 'var(--error)' }}>*</span>}
            </Label>
            <Select
              value={config[param.name] || ''}
              onChange={(e) => updateConfig(param.name, e.target.value)}
            >
              <option value="">-- {param.required ? 'Choisir un device' : 'Auto (depuis le compte)'} --</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>
                  {device.name || device.id} {device.connected ? '' : ''}
                </option>
              ))}
            </Select>
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      
      case 'range': {
        // Range field: stores "min-max" string, renders two range inputs side by side
        const rawVal = config[param.name] || param.defaultValue || '0-0';
        const parts = String(rawVal).split('-').map(v => parseInt(v) || 0);
        const rangeMin = parts[0] || 0;
        const rangeMax = parts.length > 1 ? parts[1] : rangeMin;
        return (
          <FormGroup key={param.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Label style={{ margin: 0 }}>{param.label}</Label>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', minWidth: 60, textAlign: 'right' }}>
                {rangeMin} — {rangeMax}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <input type="range" min={param.min || 0} max={param.max || 100} value={rangeMin}
                style={{ flex: 1, accentColor: 'var(--primary)' }}
                onChange={e => updateConfig(param.name, `${e.target.value}-${rangeMax}`)} />
              <input type="range" min={param.min || 0} max={param.max || 100} value={rangeMax}
                style={{ flex: 1, accentColor: 'var(--primary)' }}
                onChange={e => updateConfig(param.name, `${rangeMin}-${e.target.value}`)} />
            </div>
          </FormGroup>
        );
      }

      case 'slider': {
        const sliderVal = config[param.name] != null ? parseInt(config[param.name]) : (parseInt(param.defaultValue) || 0);
        const isPercent = param.name.includes('percentage') || param.label.includes('%');
        const suffix = isPercent ? '%' : '';
        return (
          <FormGroup key={param.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Label style={{ margin: 0 }}>{param.label}</Label>
              <span style={{ fontSize: 13, fontWeight: 700, color: sliderVal > 0 ? '#22c55e' : 'var(--text-muted)', minWidth: 40, textAlign: 'right' }}>
                {sliderVal}{suffix}
              </span>
            </div>
            <input type="range" min={param.min || 0} max={param.max || 100} step={param.step || 1} value={sliderVal}
              style={{ width: '100%', accentColor: 'var(--primary)' }}
              onChange={e => updateConfig(param.name, parseInt(e.target.value))} />
          </FormGroup>
        );
      }

      case 'toggle': {
        const toggleVal = config[param.name] != null ? config[param.name] : (param.defaultValue != null ? param.defaultValue : false);
        const isOn = toggleVal === true || toggleVal === 'true' || toggleVal === 1;
        return (
          <FormGroup key={param.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Label style={{ margin: 0 }}>{param.label}</Label>
              <button type="button" onClick={() => updateConfig(param.name, !isOn)}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: isOn ? '#22c55e' : 'var(--surface)', position: 'relative', transition: 'background .2s'
                }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
                  left: isOn ? 23 : 3, transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)'
                }} />
              </button>
            </div>
          </FormGroup>
        );
      }

      case 'method-selector': {
        const currentVal = config[param.name] || '';
        const opts = param.options || [];
        return (
          <FormGroup key={param.name}>
            <Label>{param.label}</Label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {opts.map(opt => {
                const isActive = currentVal === opt.value;
                return (
                  <button key={opt.value} type="button"
                    onClick={() => updateConfig(param.name, opt.value)}
                    style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `2px solid ${isActive ? 'var(--primary)' : 'var(--card-border)'}`,
                      background: isActive ? 'rgba(165,145,239,.15)' : 'transparent',
                      color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                      transition: 'all .15s'
                    }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </FormGroup>
        );
      }

      case 'target-selector': {
        const usePool = config._use_pool_targets || false;
        const targetsValue = config[param.name] || '';
        // Filter targets by purpose: scrape targets for scrape scripts, interact (or null) for interact
        const purpose = param.targetPurpose || 'interact';
        const filteredTargets = accountTargets.filter(t => {
          if (!t || !t.value) return false;
          if (purpose === 'scrape') return t.purpose === 'scrape';
          return t.purpose === 'interact' || !t.purpose;
        });
        const purposeLabel = purpose === 'scrape' ? 'scrape' : 'interaction';
        return (
          <FormGroup key={param.name}>
            <Label>{param.label}</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <button type="button" onClick={() => updateConfig('_use_pool_targets', !usePool)}
                style={{
                  width: 20, height: 20, borderRadius: 4, border: `2px solid ${usePool ? '#22c55e' : 'var(--card-border)'}`,
                  background: usePool ? '#22c55e' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0
                }}>
                {usePool ? '✓' : ''}
              </button>
              <span style={{ fontSize: 12, color: usePool ? '#22c55e' : 'var(--text-muted)', fontWeight: 600 }}>
                Utiliser toutes les cibles du pool ({filteredTargets.length} cible{filteredTargets.length !== 1 ? 's' : ''} de {purposeLabel})
              </span>
            </div>
            {!usePool && (
              <Input
                type="text"
                value={targetsValue}
                onChange={(e) => updateConfig(param.name, e.target.value)}
                placeholder={param.placeholder}
              />
            )}
            {usePool && filteredTargets.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 0' }}>
                {filteredTargets.slice(0, 12).map((t, i) => (
                  <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 6, background: purpose === 'scrape' ? 'rgba(59,130,246,.1)' : 'rgba(165,145,239,.1)', color: purpose === 'scrape' ? '#3b82f6' : 'var(--primary)', fontWeight: 600 }}>
                    {t.type === 'hashtag' ? '#' : '@'}{t.value}
                  </span>
                ))}
                {filteredTargets.length > 12 && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{filteredTargets.length - 12} autres</span>
                )}
              </div>
            )}
            {param.helpText && <HelpText>{param.helpText}</HelpText>}
          </FormGroup>
        );
      }

      default:
        return null;
    }
  };
  
  const NodeIcon = nodeDefinition.icon;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer $wide={node.data.type === 'code'} onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {NodeIcon && <NodeIcon size={20} color={nodeDefinition.color} />}
            Configuration - {nodeDefinition.label}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {/* ── CODE NODE: n8n-style tabbed UI ── */}
          {node.data.type === 'code' ? (
            <>
              {/* Language selector (always visible) */}
              {nodeDefinition.parameters.filter(p => p.type !== 'code-editor').map(param => renderField(param))}

              {/* Tab bar: Templates | Code | Input | Output */}
              <NodeTabBar>
                <NodeTab $active={codeTab === 'templates'} onClick={() => setCodeTab('templates')}>
                  <ChevronRight size={14} /> Templates
                </NodeTab>
                <NodeTab $active={codeTab === 'code'} onClick={() => setCodeTab('code')}>
                  <Code size={14} /> Code
                </NodeTab>
                <NodeTab $active={codeTab === 'input'} onClick={() => setCodeTab('input')}>
                  <ChevronDown size={14} /> Input
                </NodeTab>
                <NodeTab $active={codeTab === 'output'} onClick={() => setCodeTab('output')}>
                  <Eye size={14} /> Output {testOutput && (testOutput.success ? '✅' : '❌')}
                </NodeTab>
              </NodeTabBar>

              {/* ── TAB: Templates ── */}
              {codeTab === 'templates' && nodeDefinition.templates && (
                <>
                  <TemplateSectionTitle>
                    <Code size={14} /> Choisir un template de code
                  </TemplateSectionTitle>
                  <TemplateGrid>
                    {nodeDefinition.templates.map(t => (
                      <TemplateCard key={t.id} onClick={() => applyTemplate(t)}>
                        <span className="tc-label">{t.label}</span>
                        <span className="tc-desc">{t.description}</span>
                      </TemplateCard>
                    ))}
                  </TemplateGrid>
                  <HelpText style={{ textAlign: 'center' }}>
                    Cliquez sur un template pour l'insérer dans l'éditeur de code
                  </HelpText>
                </>
              )}

              {/* ── TAB: Code Editor ── */}
              {codeTab === 'code' && (
                <>
                  {nodeDefinition.parameters.filter(p => p.type === 'code-editor').map(param => renderField(param))}
                </>
              )}

              {/* ── TAB: Input Data (n8n-style) ── */}
              {codeTab === 'input' && (
                <>
                  <TemplateSectionTitle>
                    <ChevronDown size={14} /> Données d'entrée (input)
                  </TemplateSectionTitle>
                  <IOSection style={{ marginTop: 0 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--primary)' }}>
                      Comment utiliser les entrées
                    </div>
                    <div>Accédez aux données du nœud précédent via <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3, color: '#8b5cf6', fontWeight: 700 }}>input</code></div>
                    <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 11 }}>
                      Ex: <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>input.delay_seconds</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>input.random_number</code>, <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>input.counter</code>
                    </div>
                  </IOSection>
                  <InputDataPanel style={{ marginTop: 12 }}>
                    <InputDataHeader>
                      <span>📥 Données d'entrée pour le test</span>
                      <button
                        onClick={() => setEditingInput(!editingInput)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}
                      >
                        {editingInput ? <><Eye size={12} /> Aperçu</> : <><Pencil size={12} /> Modifier</>}
                      </button>
                    </InputDataHeader>
                    {editingInput ? (
                      <InputEditTextarea
                        value={inputDataStr}
                        onChange={e => setInputDataStr(e.target.value)}
                        spellCheck={false}
                        placeholder='{"key": "value"}'
                      />
                    ) : (
                      <InputDataBody>{inputDataStr || '{}'}</InputDataBody>
                    )}
                  </InputDataPanel>
                  <HelpText style={{ marginTop: 8 }}>
                    Ces données simulent l'output du nœud précédent. Modifiez-les pour tester différents scénarios.
                  </HelpText>
                </>
              )}

              {/* ── TAB: Output (test results) ── */}
              {codeTab === 'output' && (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                    <TestButton onClick={runTest} disabled={testing}>
                      <Play size={14} />
                      {testing ? 'Exécution...' : 'Tester ce nœud'}
                    </TestButton>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Exécute le code avec les données d'entrée et affiche le résultat
                    </span>
                  </div>

                  {!testOutput && !testing && (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                      <Play size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
                      <div>Cliquez sur <strong>"Tester ce nœud"</strong> pour exécuter le code et voir le résultat</div>
                      <div style={{ fontSize: 11, marginTop: 8 }}>L'output sera passé comme <code style={{ color: '#8b5cf6' }}>input</code> au nœud suivant dans le workflow</div>
                    </div>
                  )}

                  {testOutput && (
                    <>
                      <OutputPanel $error={!testOutput.success}>
                        <OutputHeader $error={!testOutput.success}>
                          <span>{testOutput.success ? '✅ Résultat (output)' : '❌ Erreur'}</span>
                          <OutputTabBar style={{ borderBottom: 'none', marginLeft: 'auto' }}>
                            <OutputTab $active={outputViewTab === 'json'} onClick={() => setOutputViewTab('json')}>JSON</OutputTab>
                            <OutputTab $active={outputViewTab === 'table'} onClick={() => setOutputViewTab('table')}>Table</OutputTab>
                          </OutputTabBar>
                        </OutputHeader>
                        {outputViewTab === 'json' ? (
                          <OutputBody>{JSON.stringify(testOutput, null, 2)}</OutputBody>
                        ) : (
                          <div style={{ padding: '8px 12px', background: 'var(--surface)', maxHeight: 200, overflow: 'auto' }}>
                            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid var(--card-border)' }}>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700, color: 'var(--text-muted)', fontSize: 11 }}>Clé</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700, color: 'var(--text-muted)', fontSize: 11 }}>Valeur</th>
                                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 700, color: 'var(--text-muted)', fontSize: 11 }}>Type</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(testOutput).map(([k, v]) => (
                                  <tr key={k} style={{ borderBottom: '1px solid var(--card-border)' }}>
                                    <td style={{ padding: '4px 8px', fontWeight: 600, color: '#8b5cf6', fontFamily: 'monospace' }}>{k}</td>
                                    <td style={{ padding: '4px 8px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                    </td>
                                    <td style={{ padding: '4px 8px', color: 'var(--text-muted)', fontSize: 11 }}>{typeof v}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </OutputPanel>
                      {consoleLogs.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>Console</div>
                          <ConsoleOutput>{consoleLogs.join('\n')}</ConsoleOutput>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {/* ── NON-CODE NODES: standard rendering ── */}
              {nodeDefinition.parameters.map(param => renderField(param))}
              {/* Dynamic script parameters for device nodes */}
              {node.data.type === 'device' && config.script_type && SCRIPT_DEFINITIONS[config.script_type] && (
                <>
                  <div style={{ borderTop: '1px solid var(--card-border)', margin: '16px 0 12px', paddingTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      ⚙️ Paramètres — {SCRIPT_DEFINITIONS[config.script_type].label}
                    </div>
                    {cardParamsLoading && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontStyle: 'italic' }}>
                        Chargement des paramètres depuis la base de données...
                      </div>
                    )}
                    {cardParams && !cardParamsLoading && (
                      <div style={{ fontSize: 10, color: '#22c55e', marginBottom: 8, fontWeight: 600 }}>
                        ✓ Paramètres chargés depuis la DB
                      </div>
                    )}
                  </div>
                  {SCRIPT_DEFINITIONS[config.script_type].parameters.map(param => renderField(param))}
                </>
              )}
              {/* I/O variable info for delay nodes */}
              {(node.data.type === 'delay') && (
                <IOSection>
                  <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--primary)' }}>ℹ️ Fonctionnement</div>
                  <div>
                    {(config.delay_mode || 'random') === 'random' 
                      ? <>En mode <strong>aléatoire</strong>, un délai entre <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>{config.delay_min || 30}s</code> et <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>{config.delay_max || 120}s</code> sera choisi à chaque exécution.</>
                      : <>En mode <strong>fixe</strong>, le workflow attendra exactement <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>{config.delay_seconds || 60}s</code>.</>
                    }
                  </div>
                  <div style={{ marginTop: 4, fontStyle: 'italic' }}>Si le nœud précédent produit <code style={{ background: 'var(--surface)', padding: '1px 4px', borderRadius: 3 }}>delay_seconds</code>, cette valeur sera utilisée à la place.</div>
                </IOSection>
              )}
              {/* Test/Run button for delay, condition nodes */}
              {['delay', 'condition'].includes(node.data.type) && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <TestButton onClick={runTest} disabled={testing}>
                    <Play size={14} />
                    {testing ? 'Exécution...' : 'Tester ce nœud'}
                  </TestButton>
                </div>
              )}
              {/* Test output for non-code nodes */}
              {testOutput && !['code'].includes(node.data.type) && (
                <OutputPanel $error={!testOutput.success}>
                  <OutputHeader $error={!testOutput.success}>
                    {testOutput.success ? '✅ Résultat (output)' : '❌ Erreur'}
                  </OutputHeader>
                  <OutputBody>
                    {JSON.stringify(testOutput, null, 2)}
                  </OutputBody>
                </OutputPanel>
              )}
            </>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button onClick={onClose}>Annuler</Button>
          <Button $primary onClick={handleSave}>Sauvegarder</Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default NodePropertiesPanel;
